import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useShallow } from 'zustand/shallow';
import { IconButton } from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import MenuButton from 'components/shared/MenuButton';
import { useObservedElementSize } from 'hooks';
import { useSelector } from 'hooks/reactRedux';

import {
  formatEntityDisplayLabel,
  getEntityIcon
} from 'components/mrp/mrp.utils';

import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import { DateNavigation } from 'components/shared/index';
import EntitySearch from 'components/mrp/components/EntitySearch/EntitySearch';

import {
  computeRange,
  toDate
} from 'components/shared/DateNavigation/DateNavigation.utils';
import { useDateNavigationHoursStore } from 'components/shared/DateNavigation/DateNavigationHours.stores';
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';

import type {
  EditAppointmentTypeResponse,
  EventResponse,
  PlanningEntityIdentifier
} from 'generated/index';
import type { SingleOrRange } from 'components/shared/DateNavigation';
import type {
  GridRowId,
  GridRowParams,
  GridRowSelectionModel
} from '@mui/x-data-grid-pro';

import {
  AppointmentsOverviewActionsContainer,
  AppointmentsOverviewContentArea,
  AppointmentsOverviewDateNavigationContainer,
  AppointmentsOverviewEntitySearchContainer,
  AppointmentsOverviewFiltersContainer,
  AppointmentsOverviewHeader,
  AppointmentsOverviewPageContainer,
  AppointmentsOverviewParticipantFilter,
  AppointmentsOverviewSelectedParticipant,
  AppointmentsOverviewSelectedParticipantLabel
} from './AppointmentsOverview.styles';
import {
  getAppointmentRowId,
  useColumnDefinitions
} from './AppointmentsOverview.utils';
import {
  APPOINTMENTS_OVERVIEW_SESSION_STORAGE_KEYS,
  PARTICIPANT_ENTITY_TYPES
} from './types';
import { useFilteredAppointments } from './hooks/useFilteredAppointments';
import { useBulkDeleteAppointments } from './hooks/useBulkDeleteAppointments';
import { useBulkEditParticipants } from './hooks/useBulkEditParticipants';
import AppointmentRowTooltip from './components/AppointmentRowTooltip';
import EditParticipantsDrawer from './components/EditParticipantsDialog/EditParticipantsDialog';
import RecurrenceConfirmationDialog from './components/RecurrenceConfirmationDialog/RecurrenceConfirmationDialog';

type SelectedParticipant = {
  planningEntity: PlanningEntityIdentifier;
  label: string;
};

const AppointmentsOverview = () => {
  const { t } = useTranslation('common');
  const hasEditAccess = useSelector(
    ({ grants }) => grants.hasAddAppointmentAccess
  );

  const {
    selectedDate,
    selectedDateRange,
    setSelectedDate,
    setSelectedDateRange
  } = useDateNavigationHoursStore(
    useShallow((state) => ({
      selectedDate: state.selectedDate,
      selectedDateRange: state.selectedDateRange,
      setSelectedDate: state.setSelectedDate,
      setSelectedDateRange: state.setSelectedDateRange
    }))
  );

  const period = useDateNavigationStore(useShallow((state) => state.period));
  const appliedPeriod = period ?? 'day';

  const [selectedParticipant, setSelectedParticipant] =
    useState<SelectedParticipant | null>(null);
  const [setEntitySearchContainerRef, entitySearchSize] =
    useObservedElementSize<HTMLDivElement>({
      selector: '.MuiAutocomplete-root',
      enabled: !selectedParticipant,
      minHeight: 56,
      minWidth: 360
    });

  // Apply a participant handed over via session storage once on mount, then clear it.
  useEffect(() => {
    const stored = window.sessionStorage.getItem(
      APPOINTMENTS_OVERVIEW_SESSION_STORAGE_KEYS.FILTERED_PARTICIPANT
    );

    if (!stored) return;

    window.sessionStorage.removeItem(
      APPOINTMENTS_OVERVIEW_SESSION_STORAGE_KEYS.FILTERED_PARTICIPANT
    );

    try {
      const participant = JSON.parse(stored) as SelectedParticipant;
      const planningEntity = participant?.planningEntity;
      const isValidParticipant =
        !!planningEntity?.entityId &&
        !!planningEntity?.planningEntityType &&
        PARTICIPANT_ENTITY_TYPES.includes(planningEntity.planningEntityType);

      if (isValidParticipant) {
        setSelectedParticipant(participant);
      }
    } catch {
      // Ignore malformed session storage payloads.
    }
  }, []);

  const { startDate, endDate } = useMemo(() => {
    if (appliedPeriod === 'custom') {
      return computeRange(selectedDateRange ?? null, appliedPeriod);
    }
    return computeRange(selectedDate, appliedPeriod);
  }, [appliedPeriod, selectedDate, selectedDateRange]);

  const {
    data = [],
    isLoading,
    isError,
    refetch
  } = useFilteredAppointments({
    startDate,
    endDate,
    planningEntity: selectedParticipant?.planningEntity
  });

  const [selectedAppointments, setSelectedAppointments] = useState<
    EventResponse[]
  >([]);

  const [editParticipantsDialogOpen, setEditParticipantsDialogOpen] =
    useState(false);
  const [deleteRecurrenceDialogOpen, setDeleteRecurrenceDialogOpen] =
    useState(false);

  const { deleteAppointments } = useBulkDeleteAppointments({
    onSuccess: () => {
      setSelectedAppointments([]);
    }
  });

  const { editParticipants, isLoading: isEditingParticipants } =
    useBulkEditParticipants({
      onSuccess: () => {
        setSelectedAppointments([]);
        setEditParticipantsDialogOpen(false);
      }
    });

  const selectionModel = useMemo<GridRowSelectionModel>(
    () => ({
      type: 'include',
      ids: new Set<GridRowId>(
        selectedAppointments.map((a) => `${a.id}-${a.date}`)
      )
    }),
    [selectedAppointments]
  );

  // Clear selection when the filter context changes
  useEffect(() => {
    setSelectedAppointments([]);
  }, [
    selectedParticipant?.planningEntity.entityId,
    selectedParticipant?.planningEntity.planningEntityType,
    startDate,
    endDate
  ]);

  const handleSelectedDateChange = useCallback<(date: SingleOrRange) => void>(
    (date) => {
      if (appliedPeriod === 'custom') {
        setSelectedDateRange(Array.isArray(date) ? date : undefined);
        return;
      }

      if (Array.isArray(date) || !date) {
        setSelectedDate(null);
        return;
      }

      setSelectedDate(toDate(date));
    },
    [appliedPeriod, setSelectedDate, setSelectedDateRange]
  );

  const handleEntityChange = useCallback(
    (entity: PlanningEntityIdentifier, label: string) => {
      setSelectedParticipant({ planningEntity: entity, label });
    },
    []
  );

  const handleClearParticipant = useCallback(() => {
    setSelectedParticipant(null);
  }, []);

  const handleSelectionChange = useCallback(
    (selectionModel: GridRowSelectionModel) => {
      const selected = data.filter((a) =>
        selectionModel.ids.has(`${a.id}-${a.date}`)
      );
      setSelectedAppointments(selected);
    },
    [data]
  );

  const handleDeleteClick = useCallback(() => {
    const hasRecurring = selectedAppointments.some((a) => a.isRecurring);
    if (hasRecurring) {
      setDeleteRecurrenceDialogOpen(true);
    } else {
      deleteAppointments(selectedAppointments, 'ONLY_THIS');
    }
  }, [deleteAppointments, selectedAppointments]);

  const handleDeleteRecurrenceConfirm = useCallback(
    (editAppointmentType: EditAppointmentTypeResponse) => {
      deleteAppointments(selectedAppointments, editAppointmentType);
      setDeleteRecurrenceDialogOpen(false);
    },
    [deleteAppointments, selectedAppointments]
  );

  const handleDeleteRecurrenceCancel = useCallback(() => {
    setDeleteRecurrenceDialogOpen(false);
  }, []);

  const handleEditParticipantsClick = useCallback(() => {
    setEditParticipantsDialogOpen(true);
  }, []);

  const handleEditParticipantsConfirm = useCallback(
    (
      addParticipants: PlanningEntityIdentifier[],
      removeParticipants: PlanningEntityIdentifier[],
      editAppointmentType: EditAppointmentTypeResponse
    ) => {
      editParticipants(
        selectedAppointments,
        addParticipants,
        removeParticipants,
        editAppointmentType
      );
    },
    [editParticipants, selectedAppointments]
  );

  const handleEditParticipantsClose = useCallback(() => {
    setEditParticipantsDialogOpen(false);
  }, []);

  const columns = useColumnDefinitions();

  const dataById = useMemo(
    () => new Map(data.map((a) => [`${a.id}-${a.date}`, a])),
    [data]
  );

  const defaultDate = useMemo(
    () => (selectedDate ? selectedDate : dayjs().startOf('day').toDate()),
    [selectedDate]
  );

  return (
    <AppointmentsOverviewPageContainer>
      <AppointmentsOverviewContentArea>
        <AppointmentsOverviewHeader>
          <AppointmentsOverviewFiltersContainer>
            <AppointmentsOverviewParticipantFilter>
              {selectedParticipant ? (
                <AppointmentsOverviewSelectedParticipant>
                  {getEntityIcon(
                    selectedParticipant.planningEntity.planningEntityType,
                    undefined,
                    'MEDIUM',
                    true
                  )}
                  <AppointmentsOverviewSelectedParticipantLabel>
                    {formatEntityDisplayLabel(
                      selectedParticipant.label,
                      selectedParticipant.planningEntity.number
                    )}
                  </AppointmentsOverviewSelectedParticipantLabel>
                  <IconButton
                    size="small"
                    onClick={handleClearParticipant}
                    aria-label={t(
                      'appointmentsOverview.selectedParticipantLabel'
                    )}
                  >
                    <Icon type="xmark" fontSize="small" />
                  </IconButton>
                </AppointmentsOverviewSelectedParticipant>
              ) : (
                <AppointmentsOverviewEntitySearchContainer
                  ref={setEntitySearchContainerRef}
                  reservedHeight={entitySearchSize.height}
                  reservedWidth={entitySearchSize.width}
                >
                  <EntitySearch
                    entityTypeFilters={PARTICIPANT_ENTITY_TYPES}
                    multiple={false}
                    minWidth={360}
                    onChange={handleEntityChange}
                  />
                </AppointmentsOverviewEntitySearchContainer>
              )}
            </AppointmentsOverviewParticipantFilter>

            <AppointmentsOverviewDateNavigationContainer>
              <DateNavigation
                allowMenuOptions
                defaultDate={defaultDate}
                defaultDateRange={selectedDateRange}
                defaultPeriod="day"
                onDateChange={handleSelectedDateChange}
                options={['day', 'week', 'month', 'custom']}
              />
            </AppointmentsOverviewDateNavigationContainer>

            {hasEditAccess && (
              <AppointmentsOverviewActionsContainer>
                <MenuButton
                  label={t('actions')}
                  disabled={selectedAppointments.length === 0}
                  options={[
                    {
                      label: t(
                        'appointmentsOverview.editParticipants.buttonWithCount',
                        { count: selectedAppointments.length }
                      ),
                      onClick: handleEditParticipantsClick,
                      icon: 'user-pen'
                    },
                    {
                      label: t('appointmentsOverview.delete.buttonWithCount', {
                        count: selectedAppointments.length
                      }),
                      onClick: handleDeleteClick,
                      icon: 'trash-can'
                    }
                  ]}
                />
              </AppointmentsOverviewActionsContainer>
            )}
          </AppointmentsOverviewFiltersContainer>
        </AppointmentsOverviewHeader>

        <TableCRUDLayout
          columns={columns}
          rows={data}
          loading={isLoading}
          hasLoadingError={isError}
          onLoadingError={refetch}
          getRowId={getAppointmentRowId}
          translationKey={
            selectedParticipant
              ? undefined
              : 'appointmentsOverview.selectParticipantHint'
          }
          initialState={{
            sorting: {
              sortModel: [{ field: 'appointmentDate', sort: 'asc' }]
            }
          }}
          editable={false}
          hideCreateAction
          checkboxSelection={hasEditAccess}
          isRowSelectableFromParent={(params: GridRowParams) =>
            params.row.isEditable !== false
          }
          customSelectionModel={selectionModel}
          onSelectionChange={handleSelectionChange}
          slots={{
            row: (gridRow) => (
              <AppointmentRowTooltip
                event={dataById.get(`${gridRow.row?.id}-${gridRow.row?.date}`)}
                gridRow={gridRow}
              />
            )
          }}
        />
      </AppointmentsOverviewContentArea>

      <EditParticipantsDrawer
        open={editParticipantsDialogOpen}
        isLoading={isEditingParticipants}
        selectedAppointments={selectedAppointments}
        onConfirm={handleEditParticipantsConfirm}
        onClose={handleEditParticipantsClose}
      />

      <RecurrenceConfirmationDialog
        open={deleteRecurrenceDialogOpen}
        title={t('appointmentsOverview.delete.dialog.title', {
          count: selectedAppointments.length
        })}
        description={t('appointmentsOverview.delete.dialog.recurrenceWarning')}
        confirmLabel={t('appointmentsOverview.delete.button')}
        onConfirm={handleDeleteRecurrenceConfirm}
        onCancel={handleDeleteRecurrenceCancel}
      />
    </AppointmentsOverviewPageContainer>
  );
};

export default AppointmentsOverview;
